import {useDeepMemo} from '@wandb/weave/common/state/hooks';
import {useCallback, useMemo, useState} from 'react';

import * as Generated from '../../../generated/graphql';
import {
  useBulkEditorProjectTagsQuery,
  useRunSelectorQuery,
} from '../../../state/graphql/runSelectorQuery';
import {useSelectedCounts} from '../../../state/graphql/selectedCountQuery';
import {useDispatch} from '../../../state/hooks';
import {useRunsQueryContext} from '../../../state/runs/hooks';
import * as RunHooks from '../../../state/runs/hooks';
import {setCreateSweepArgs as setCreateSweepArgsAction} from '../../../state/sweeps/actions';
import * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as FilterActions from '../../../state/views/filter/actions';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import * as ViewHooks from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {useRunSet, useRunSetProject} from '../../../state/views/runSet/hooks';
import * as RunSetTypes from '../../../state/views/runSet/types';
import * as SortActions from '../../../state/views/sort/actions';
import * as TempSelectionsActions from '../../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../../state/views/tempSelections/types';
import {
  Config as TableSettings,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../../util/runfeed';
import * as TableCols from '../../../util/tablecols';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {usePublicQueueAccess} from '../../Launch/hooks';
import {JobInfo, LaunchDrawerJobRun} from '../../Launch/utilTypes';
import {useAddToHistory} from '../../PanelBank/PanelBank';
import type {RunSearchHistoryItem} from '../../PanelBank/types';
import {useCombinedFilter} from './useCombinedFilter';
import {useDefaultKeys} from './useDefaultKeys';
import {useFilteredKeys} from './useFilteredKeys';
import {useRunSelectorRuns} from './useRunSelectorRuns';
import {useTopLevelQueryVariables} from './useTopLevelQueryVariables';

interface UseRunSelectorPropsOptions {
  customRunNamesRef: CustomRunNamesTypes.Ref;
  isExpandable?: boolean;
  isExpanded?: boolean;
  isInReport?: boolean;
  runSetRef: RunSetTypes.Ref;
  showArtifactCounts?: boolean;
  tempSelectionsRef: TempSelectionsTypes.Ref;
}

export function useRunSelectorProps(props: UseRunSelectorPropsOptions) {
  const runSet = ViewHooks.usePart(props.runSetRef);
  const runSetFull = useRunSet(props.runSetRef);
  const customRunNames = ViewHooks.usePartMaybe(props.customRunNamesRef);
  const groupSelections = ViewHooks.usePart(runSet.groupSelectionsRef);
  const tempSelections = ViewHooks.usePart(props.tempSelectionsRef);
  const runSetFilters = ViewHooks.usePart(runSet.filtersRef);
  const sort = runSetFull.sort;
  const truncationType =
    runSetFull.runNameTruncationType ?? TruncationType.Middle;

  const toggleExpandedRow = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.toggleExpandedRowAddress
  );

  const tempSelectAll = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAll,
    props.tempSelectionsRef,
    runSet.groupSelectionsRef
  );

  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    runSet.groupSelectionsRef
  );

  const {entityName, projectName, projectInternalId} = useRunSetProject(
    runSet.project
  );

  const groupTempSelection = useMemo(
    () => ({
      grouping: groupSelections.grouping,
      selections: tempSelections,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
    }),
    [
      groupSelections.grouping,
      groupSelections.expandedRowAddresses,
      tempSelections,
    ]
  );

  const {mergeFilters} = useRunsQueryContext();
  const runSelectorQuery = useRunSelectorQuery(projectInternalId);
  const bulkEditorProjectTagsQuery = useBulkEditorProjectTagsQuery({
    entityName,
    projectName,
    filters: runSetFilters,
    mergeFilters,
    groupTempSelection,
    ...props,
  });
  const {tempSelectedCount, tempOutputArtifactsCount, tempSelectedActiveCount} =
    useSelectedCounts(
      props.runSetRef,
      props.tempSelectionsRef,
      props.customRunNamesRef
    );

  const [stopRun] = Generated.useStopRunMutation();
  const updateRun = RunHooks.useUpdateRun();
  const bulkUpdateRunTags = RunHooks.useBulkUpdateRunTags();
  const [moveRuns] = Generated.useMoveRunsMutation();
  const deleteRuns = RunHooks.useDeleteRuns();
  const [updateGroupRun] = Generated.useUpsertRunGroupMutation();

  const runSetUpdate = ViewHooks.useViewAction(
    props.runSetRef,
    RunSetActions.update
  );

  const setGrouping = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.setGrouping
  );

  const setSort = ViewHooks.useViewAction(runSet.sortRef, SortActions.set);

  const selectAll = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.selectAll
  );

  const selectNone = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.selectNone
  );

  const setProject = useCallback(
    (newEntityName: string, newProjectName: string, newProjectId: string) =>
      runSetUpdate({
        project: {
          entityName: newEntityName,
          name: newProjectName,
          id: newProjectId,
        },
      }),
    [runSetUpdate]
  );

  const setFilters = ViewHooks.useViewAction(
    runSet.filtersRef,
    FilterActions.set
  );

  const setSearch = useCallback(
    (query: string, isRegex: boolean) =>
      runSetUpdate({search: {query, isRegex}}),
    [runSetUpdate]
  );

  const setSearchHistory = useCallback(
    (searchHistory: RunSearchHistoryItem[] | undefined) =>
      runSetUpdate({searchHistory}),
    [runSetUpdate]
  );

  const addToHistory = useAddToHistory(setSearchHistory, runSet.searchHistory);

  const setTruncationType = useCallback(
    (newTruncationType: TruncationType) =>
      runSetUpdate({runNameTruncationType: newTruncationType}),
    [runSetUpdate]
  );

  const setTableSettings = useCallback(
    (runFeed: TableSettings) => runSetUpdate({runFeed}),
    [runSetUpdate]
  );

  const defaultKeys = useDefaultKeys();
  const filteredKeys = useFilteredKeys(runSelectorQuery);

  const tableSettings = useDeepMemo(
    useMemo(() => {
      return TableCols.cleanRunFeedConfig(
        runSet.runFeed || EMPTY_TABLE_SETTINGS,
        filteredKeys,
        defaultKeys,
        props.isInReport
      );
    }, [defaultKeys, filteredKeys, props.isInReport, runSet.runFeed])
  );

  const [launchDrawerJobRun, setLaunchDrawerJobRun] =
    useState<LaunchDrawerJobRun | null>(null);
  const setLaunchDrawerRow = useCallback((name: string, jobInfo?: JobInfo) => {
    if (jobInfo == null) {
      return;
    }
    setLaunchDrawerJobRun({
      runName: name,
      jobId: jobInfo.id,
      jobVersion: jobInfo.versionStr,
    });
  }, []);

  const closeLaunchConfigModal = useCallback(() => {
    setLaunchDrawerJobRun(null);
  }, []);

  const launchAgentQuery = Generated.useEntityAgentsQuery({
    variables: {entityName},
  });
  const isLaunchActive = useMemo(() => {
    return (
      launchAgentQuery.data?.entity?.launchProject?.launchAgents?.some(
        agent => {
          return ['POLLING', 'RUNNING'].includes(agent.agentStatus);
        }
      ) ?? false
    );
  }, [launchAgentQuery]);

  const {hasPublicQueueAccess} = usePublicQueueAccess();

  const dispatch = useDispatch();

  const isTableCollapsed = (props.isExpandable && !props.isExpanded) ?? false;

  const topLevelQueryVariables = useTopLevelQueryVariables({
    customRunNames,
    enableArtifactCounts: props.showArtifactCounts ?? false,
    entityName,
    groupSelections,
    isTableCollapsed,
    mergeFilters,
    projectInternalId,
    projectName,
    runSet,
    runSetFilters,
    sort,
    tableSettings,
  });

  const {
    isLoadingRuns,
    isLoadingRunsPolled,
    loadMoreRuns,
    runs,
    pinnedRuns,
    totalRuns,
  } = useRunSelectorRuns({
    isRootQuery: true,
    queryId: props.runSetRef.id,
    pageSize: tableSettings.pageSize || EMPTY_TABLE_SETTINGS.pageSize,
    partialVariables: topLevelQueryVariables,
    shouldOnlyShowSelected: Boolean(tableSettings.onlyShowSelected),
    skip: runSelectorQuery.initialLoading,
  });

  const combinedRuns = useMemo(() => {
    return [...pinnedRuns, ...runs];
  }, [runs, pinnedRuns]);

  const horizontalRuleIndex =
    pinnedRuns.length > 0 ? pinnedRuns.length - 1 : undefined;

  const combinedFilter = useCombinedFilter({
    userFilters: runSetFilters,
    groupSelection: groupTempSelection,
    mergeFilters,
    search: runSet.search,
    customRunNames,
  });

  const setCreateSweepArgs = useCallback(() => {
    dispatch(
      setCreateSweepArgsAction({
        runSet: runSetFull,
        tempSelections,
      })
    );
  }, [dispatch, runSetFull, tempSelections]);

  return {
    addToHistory,
    bulkEditorProjectTagsQuery,
    bulkUpdateRunTags,
    closeLaunchConfigModal,
    combinedFilter,
    defaultKeys,
    deleteRuns,
    entityName,
    filteredKeys,
    filters: runSetFilters,
    groupSelections,
    groupTempSelection,
    horizontalRuleIndex,
    launchDrawerJobRun,
    loadMoreRuns,
    isLaunchActive,
    isLaunchPublicQueueActive: hasPublicQueueAccess,
    isLoadingRuns,
    isLoadingRunsPolled,
    isTableCollapsed,
    moveRuns,
    projectInternalId,
    projectName,
    runs: combinedRuns,
    runSelectorQuery,
    runSet,
    selectAll,
    selectNone,
    setCreateSweepArgs,
    setFilters,
    setGrouping,
    setLaunchDrawerRow,
    setProject,
    setSearch,
    setTruncationType,
    setSort,
    truncationType,
    setTableSettings,
    sort,
    stopRun,
    tableSettings,
    tempOutputArtifactsCount,
    tempSelectAll,
    tempSelectedActiveCount,
    tempSelectedCount,
    tempSelectNone,
    toggleExpandedRow,
    topLevelQueryVariables,
    totalRuns,
    updateGroupRun,
    updateRun,
  };
}

export type RunSelectorPropsHook = ReturnType<typeof useRunSelectorProps>;
